import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  disconnectConnection,
  listConnections,
  renameConnection,
  type ConnectionWithSource,
} from '../../api/connections';
import { apiErrorMessage } from '../../api/errorMessage';
import StatusMessage from '../../components/StatusMessage';
import UnavailableAction from '../../components/UnavailableAction';

type PendingAction = { kind: 'disconnect' | 'destroy'; item: ConnectionWithSource };

const APP_SCHEME = import.meta.env.VITE_APP_SCHEME ?? 'meridian';

export default function ConnectionsPage() {
  const [items, setItems] = useState<ConnectionWithSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busyId, setBusyId] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await listConnections());
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load connected services.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function runAction(action: PendingAction) {
    const id = action.item.connection.id;
    setPending(null);
    setBusyId(id);
    setError('');
    setSuccess('');
    try {
      if (action.kind === 'disconnect') {
        await disconnectConnection(id);
        setSuccess(`${action.item.connection.name} was disconnected. Its existing Meridian data remains in place.`);
        await reload();
      }
    } catch (err) {
      setError(apiErrorMessage(err, `Could not ${action.kind} this service.`));
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="account-page-content">
      <div className="page-heading"><p className="eyebrow">Integrations</p><h1>Connections</h1><p>See what Meridian syncs and decide whether to pause or permanently remove access.</p></div>
      {error && <StatusMessage kind="error">{error}</StatusMessage>}
      {success && <StatusMessage kind="success">{success}</StatusMessage>}

      <section className="card">
        <div className="section-heading">
          <div><h2>Connected services</h2><p>Disconnect preserves imported data. Permanent removal opens Meridian so encrypted data can be removed from this Mac too.</p></div>
          <button className="btn btn-secondary btn-sm" type="button" onClick={() => void reload()} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
        {loading && items.length === 0 ? (
          <LoadingRows />
        ) : items.length === 0 ? (
          <div className="empty-state"><strong>No connected services</strong><p>Connections created in the Meridian app will appear here.</p></div>
        ) : (
          <ul className="connection-list" aria-label="Connected services">
            {items.map((item) => (
              <ConnectionRow
                key={item.connection.id}
                item={item}
                busy={busyId === item.connection.id}
                onAction={(kind) => setPending({ kind, item })}
                onRenamed={(updated) => setItems((current) => current.map((candidate) => candidate.connection.id === updated.connection.id ? updated : candidate))}
                onError={setError}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <UnavailableAction label="Connect another service" detail="New OAuth connections must currently be added in the Meridian app. Browser OAuth completion is not yet supported safely." />
      </section>

      {pending && <ConnectionDialog action={pending} onCancel={() => setPending(null)} onDisconnect={() => void runAction(pending)} />}
    </div>
  );
}

function ConnectionRow({ item, busy, onAction, onRenamed, onError }: {
  item: ConnectionWithSource;
  busy: boolean;
  onAction: (kind: PendingAction['kind']) => void;
  onRenamed: (updated: ConnectionWithSource) => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.connection.name);
  const [saving, setSaving] = useState(false);
  const status = item.connection.status?.toLowerCase() || 'unknown';
  const disconnected = status === 'inactive' || status === 'disconnected';

  async function save(event: FormEvent) {
    event.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) {
      onError('Connection name cannot be empty.');
      return;
    }
    setSaving(true);
    onError('');
    try {
      onRenamed(await renameConnection(item.connection.id, cleanName));
      setEditing(false);
    } catch (err) {
      onError(apiErrorMessage(err, 'Could not rename this connection.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="connection-row">
      <div className="provider-mark" aria-hidden="true">{item.source.name.slice(0, 1).toUpperCase()}</div>
      <div className="connection-main">
        {editing ? (
          <form className="inline-edit" onSubmit={save}>
            <label className="sr-only" htmlFor={`connection-${item.connection.id}`}>Connection name</label>
            <input id={`connection-${item.connection.id}`} value={name} onChange={(event) => setName(event.target.value)} maxLength={120} autoFocus disabled={saving} />
            <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => { setName(item.connection.name); setEditing(false); }} disabled={saving}>Cancel</button>
          </form>
        ) : (
          <div className="connection-title-line"><strong>{item.connection.name}</strong><span className={`status-pill ${disconnected ? 'neutral' : 'positive'}`}>{humanStatus(status)}</span></div>
        )}
        <span className="connection-provider">{item.source.name}</span>
        <div className="capability-list" aria-label="Capabilities">
          {item.source.capabilities.map((capability) => <span key={capability.id}>{capability.name}</span>)}
        </div>
        <span className="connection-meta">{item.connection.last_sync_at ? `Last synced ${formatDate(item.connection.last_sync_at)}` : 'No successful sync reported'}</span>
      </div>
      {!editing && <div className="connection-actions">
        <button className="btn btn-secondary btn-sm" type="button" onClick={() => setEditing(true)} disabled={busy}>Rename</button>
        {!disconnected && <button className="btn btn-secondary btn-sm" type="button" onClick={() => onAction('disconnect')} disabled={busy}>Disconnect</button>}
        <button className="btn btn-danger btn-sm" type="button" onClick={() => onAction('destroy')} disabled={busy}>Remove</button>
      </div>}
    </li>
  );
}

function ConnectionDialog({ action, onCancel, onDisconnect }: { action: PendingAction; onCancel: () => void; onDisconnect: () => void }) {
  const disconnecting = action.kind === 'disconnect';
  const nativeURL = `${APP_SCHEME}://account/connections?connection=${encodeURIComponent(action.item.connection.id)}`;
  return (
    <div className="device-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="connection-dialog-title">
      <div className="device-confirm-card">
        <h3 id="connection-dialog-title">{disconnecting ? 'Disconnect this service?' : 'Permanently remove this service?'}</h3>
        <p>{disconnecting
          ? `${action.item.connection.name} will stop syncing and its credentials will be removed. Data already in Meridian stays at its current point in time.`
          : 'Permanent removal must finish in Meridian so its encrypted local data can be identified and deleted safely. Meridian will show the full destructive scope and require confirmation.'}</p>
        <div className="form-actions">
          {disconnecting ? (
            <button className="btn btn-primary btn-fit" type="button" onClick={onDisconnect} autoFocus>Disconnect</button>
          ) : (
            <a className="btn btn-danger" href={nativeURL}>Open Meridian to remove</a>
          )}
          <button className="btn btn-secondary" type="button" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function LoadingRows() {
  return <div className="skeleton-list" aria-label="Loading connected services"><span /><span /><span /></div>;
}

function humanStatus(status: string): string {
  if (status === 'active') return 'Connected';
  if (status === 'pending') return 'Needs setup';
  if (status === 'inactive' || status === 'disconnected') return 'Disconnected';
  return 'Status unavailable';
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'at an unknown time' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}
